

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Snackbar,
    Alert,
    Autocomplete,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewReferralCodeModal = ({ isOpen, onClose, onSave }) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        referrer_id: null,
        status: 'Active',
        expiration_date: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get('/accounts/');
            setAccounts(response.data.map(acc => ({ value: acc.id, label: acc.name || `Account ${acc.id}` })));
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.code) errors.code = 'Code is required';
        if (!formData.referrer_id) errors.referrer_id = 'Referrer is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                expiration_date: formData.expiration_date || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                code: '',
                referrer_id: null,
                status: 'Active',
                expiration_date: '',
            });
        } catch (error) {
            console.error('Error saving Referral Code:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Referral Code',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '600px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Referral Code</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Code"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        error={!!formErrors.code}
                                        helperText={formErrors.code}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={accounts}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={accounts.find(acc => acc.value === formData.referrer_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, referrer_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Referrer"
                                                required
                                                error={!!formErrors.referrer_id}
                                                helperText={formErrors.referrer_id}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Suspended">Suspended</MenuItem>
                                            <MenuItem value="Expired">Expired</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expiration Date"
                                        name="expiration_date"
                                        type="date"
                                        value={formData.expiration_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <MDButton onClick={onClose} variant="outlined" color="secondary">
                                    Cancel
                                </MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </MDButton>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Modal>
            <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewReferralCodeModal;

